/**
 * AudioEngine.ts
 * =============================================================================
 * REWRITTEN: Robust audio engine with operation queue and state machine.
 * Eliminates race conditions, audio glitches, and ghost navigation.
 *
 * Key Architecture:
 * - Operation Queue: All audio operations are serialized
 * - State Machine: Explicit states (idle, playing, paused, transitioning)
 * - Promise-based fades: Real async fades that resolve AFTER completion
 * - Clean separation: load/play/stop/pause are independent operations
 *
 * @author Neuro-Audio Lab, Daniel Domínguez
 * @version 3.1.0 - Fix race conditions, stale buffers, and zombie nodes
 *
 * CHANGELOG v3.1.0:
 * - FIX Operation Queue: Rewrote as drainQueue() while-loop (old processQueue only ran 1 op)
 * - FIX playId: executePlay() no longer increments currentPlayId (was destroying in-flight loads)
 * - NEW loadedForPlayId: Generation tag on TrackState to detect stale buffers from old modes
 * - NEW Auto-start: loadTrack/loadEntrainment/loadTrigger auto-start source if engine is playing
 * - GUARD: startAmbienceSource/startEntrainmentSource/startTriggerSource validate generation
 * - FIX hardClearAllBuffers: Now resets loadedForPlayId on all tracks
 * - FIX dispose: Resets all generation tags
 *
 * CHANGELOG v3.0.0:
 * - FIX "Zombie Ambience": Player.tsx now calls clearAmbience() for modes without ambience
 * - executePlay() / executePlayFromPaused(): Defensive ambience sourceNode kill before start
 * - FIX "Loop Click": Both ambience and entrainment now use 3s linearRamp fade-in
 * - startAmbienceSource() rewritten to match entrainment pattern (self-managed 3s fade)
 * - fadeInAll() no longer touches ambience or entrainment (both self-manage)
 * - executeResume() now uses 3s linearRamp on master gain (was 0.15s exponentialRamp)
 * - Renamed ENTRAINMENT_FADE_IN → LOOP_FADE_IN (shared by all looping tracks)
 * - ZERO manual loop management: native loop=true only, no onended/setTimeout on loops
 *
 * CHANGELOG v2.9.0:
 * - Added triggerBuffer and triggerNode for one-shot Pavlovian anchor sounds
 * - loadTrigger(url, playId?): fetch/decode with Generation ID stale detection
 * - startTriggerSource(): plays once (loop=false), connects to master gain
 * - No fade applied - the audio file provides its own natural envelope
 * - Cleanup in stopSourcesImmediate() and dispose()
 * - Sleep mode: sleep_pad.ogg (looping drone) + sleep_trigger.ogg (bell one-shot)
 *
 * CHANGELOG v2.8.0:
 * - PIVOT: Replaced binaural oscillator generation with file-based entrainment track
 * - New 'entrainment' track alongside ambience and voice (loaded from .ogg files)
 * - Added loadEntrainment(url, playId?) with Generation ID stale detection
 * - startEntrainmentSource(): AudioBufferSourceNode with loop=true + 3s fade-in
 * - Removed BinauralState, BINAURAL_PRESETS, initializeBinauralNodes(), startBinauralSource()
 * - 'tone' TrackId still works (maps to entrainment gainNode internally)
 * - Modes without an entrainment file simply play silence on that layer
 *
 * CHANGELOG v2.7.1:
 * - ZOMBIE PAUSE FIX: Removed hardClearAllBuffers() from executePlayFromPaused()
 * - When changing modes from pause, new mode's buffers are already loaded
 * - Clearing them caused ambience to be missing (only binaurals would play)
 * - Now only stops old sources, leaving new buffers intact
 *
 * CHANGELOG v2.7.0:
 * - CLICK FIX: All gain transitions now use smooth ramps (no instant sets)
 * - executePause(): Smooth fade out before suspend, removed broken pre-schedule
 * - executeResume(): Quick ramp to 0 before resume, then smooth fade in
 * - executePlayFromPaused(): Checks if ambience loaded, falls back to full play if not
 * - All master gain changes use linearRampToValueAtTime for artifact-free transitions
 *
 * CHANGELOG v2.6.0:
 * - Added unlock() method with "Silent Unlocker" pattern for iOS Safari
 * - Plays 0.001s silent buffer to force hardware wake-up on user gesture
 * - Must be called as FIRST line in click/touch handlers (synchronous)
 * - Deprecated resumeContext() in favor of unlock() (backwards compatible)
 *
 * CHANGELOG v2.5.0:
 * - Added Generation ID pattern to detect and abort stale audio loads
 * - loadTrack() now accepts optional playId for validation
 * - Stale loads are silently discarded after fetch/decode completes
 * - Prevents audio bleeding when user rapidly changes modes
 *
 * CHANGELOG v2.4.0:
 * - MOBILE FIX: Explicit AudioContext resume at earliest moment (iOS/Android)
 * - BLEEDING FIX: Scorched earth policy for ambience cleanup on mode change
 * - Fast fade out (0.1s) on ambience before stopping to prevent clicks
 * - Immediate buffer nullification to prevent old audio reuse
 * - Added context resume checks to all playback entry points
 *
 * CHANGELOG v2.1.0:
 * - Added clearTrack() method to explicitly clear track buffers
 * - Added playFromPaused() method for clean transitions from paused state
 * - Fixed fade timing issues when AudioContext is suspended
 */

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

export type AudioMode = 'focus' | 'sleep' | 'anxiety';
export type TrackId = 'ambience' | 'tone' | 'voice';

/**
 * Engine state machine states
 */
export type EngineState = 'idle' | 'playing' | 'paused' | 'transitioning';

/**
 * Operation types for the queue
 */
type OperationType = 'play' | 'pause' | 'resume' | 'stop' | 'changeMode' | 'loadTrack' | 'playFromPaused';

interface QueuedOperation {
  type: OperationType;
  payload?: unknown;
  // [v3.1] The executor is stored WITH the operation so the drain loop
  // can call it later. Previously, the executor was only passed to
  // processQueue() at enqueue time, so only the FIRST operation ever ran.
  executor: () => Promise<unknown>;
  resolve: () => void;
  reject: (error: Error) => void;
}

export interface AudioLoadConfig {
  url: string;
  loop?: boolean;
  volume?: number;
}

interface TrackState {
  gainNode: GainNode | null;
  sourceNode: AudioBufferSourceNode | null;
  buffer: AudioBuffer | null;
  isLoaded: boolean;
  // [v3.1] Generation tag: the playId that was current when this buffer was loaded.
  // Used to detect stale buffers from a previous mode. If this doesn't match
  // currentPlayId, the buffer belongs to an old mode and must NOT be played.
  loadedForPlayId: number;
}

export interface EngineOptions {
  masterVolume?: number;
  sampleRate?: number;
}

export type EngineStateCallback = (state: EnginePublicState) => void;

export interface EnginePublicState {
  isInitialized: boolean;
  engineState: EngineState;
  isPlaying: boolean;
  isPaused: boolean;
  currentMode: AudioMode | null;
  isTransitioning: boolean;
  tracks: {
    ambience: { volume: number; isLoaded: boolean };
    tone: { volume: number; isLoaded: boolean }; // [v2.8] Now file-based entrainment
    voice: { volume: number; isLoaded: boolean };
  };
  master: { volume: number };
}

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Fade durations in seconds - using Web Audio API time constants
 */
const FADE = {
  IN: 0.15,
  OUT: 0.15,
  CROSSFADE: 0.25,
  INSTANT: 0.02,
  // [v2.1] Shorter fade for transitions from paused state
  QUICK: 0.08,
} as const;

/**
 * Safety buffer after fade operations (ms)
 */
const FADE_BUFFER_MS = 30;

/**
 * [v3.0] Fade-in duration for all looping tracks (ambience + entrainment).
 * Both tracks use linearRampToValueAtTime over this duration.
 * This eliminates click artifacts at loop start and on resume.
 */
const LOOP_FADE_IN = 3.0;

/**
 * [v3.4] Fade duration for voice mute/unmute toggle (seconds).
 * Short enough to feel responsive, long enough to avoid clicks.
 */
const VOICE_MUTE_FADE = 0.5;

/**
 * [v3.5] Default fade duration for Pomodoro phase transitions (seconds).
 * 5 seconds provides a perceptually smooth crossfade that avoids clicks
 * and gives the listener time to register the acoustic shift.
 */
const POMODORO_TRANSITION_FADE = 5.0;

// =============================================================================
// AUDIO ENGINE CLASS
// =============================================================================

export class AudioEngine {
  // Singleton
  private static instance: AudioEngine | null = null;

  public static getInstance(): AudioEngine {
    if (!AudioEngine.instance) {
      AudioEngine.instance = new AudioEngine();
    }
    return AudioEngine.instance;
  }

  public static resetInstance(): void {
    if (AudioEngine.instance) {
      AudioEngine.instance.dispose();
      AudioEngine.instance = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Core Audio Nodes
  // ---------------------------------------------------------------------------

  private audioContext: AudioContext | null = null;
  private masterGainNode: GainNode | null = null;
  private analyserNode: AnalyserNode | null = null;

  // ---------------------------------------------------------------------------
  // Track States
  // ---------------------------------------------------------------------------

  // [v2.8] All three audio layers are now file-based
  // 'entrainment' replaces the old oscillator-based binaural system
  private tracks: Record<'ambience' | 'voice' | 'entrainment', TrackState> = {
    ambience: { gainNode: null, sourceNode: null, buffer: null, isLoaded: false, loadedForPlayId: 0 },
    voice: { gainNode: null, sourceNode: null, buffer: null, isLoaded: false, loadedForPlayId: 0 },
    entrainment: { gainNode: null, sourceNode: null, buffer: null, isLoaded: false, loadedForPlayId: 0 },
  };

  // [v2.9] Pavlovian trigger - one-shot anchor sound (e.g. Tibetan bowl)
  // Separate from the looping tracks; played once when mode starts
  private triggerBuffer: AudioBuffer | null = null;
  private triggerNode: AudioBufferSourceNode | null = null;
  private triggerLoadedForPlayId: number = 0; // [v3.1] Generation tag for trigger
  // [v3.3] Session guard: tracks whether the trigger has already played in the
  // current session. Prevents the Pavlovian bell from re-firing on component
  // remount (e.g., navigating to /science and back while audio is playing).
  // Reset on setMode() and executeStop() — a new mode or new session allows it.
  private triggerHasPlayedForSession: boolean = false;

  // ---------------------------------------------------------------------------
  // Engine State
  // ---------------------------------------------------------------------------

  private isInitialized: boolean = false;
  private engineState: EngineState = 'idle';
  private currentMode: AudioMode | null = null;

  // [v2.3] Transition lock to prevent concurrent mode changes
  private _isTransitioning: boolean = false;

  // [v2.5] Generation ID for stale request detection
  // Incremented on each play() call, used to abort outdated loads
  private currentPlayId: number = 0;

  private volumes = {
    master: 0.8,
    ambience: 0.7,
    tone: 0.3,
    voice: 0.9,
  };

  // [v3.4] Voice mute state for guide voice toggle (anxiety mode)
  private voiceMuted: boolean = false;

  // ---------------------------------------------------------------------------
  // Operation Queue - CRITICAL for preventing race conditions
  // ---------------------------------------------------------------------------

  private operationQueue: QueuedOperation[] = [];
  private isProcessingQueue: boolean = false;

  // ---------------------------------------------------------------------------
  // Subscribers
  // ---------------------------------------------------------------------------

  private stateCallbacks: Set<EngineStateCallback> = new Set();

  // ---------------------------------------------------------------------------
  // Constructor
  // ---------------------------------------------------------------------------

  private constructor() {
  }

  // ===========================================================================
  // OPERATION QUEUE SYSTEM
  // ===========================================================================

  /**
   * Enqueue an operation and return a Promise that resolves when complete.
   * All audio operations MUST go through this queue to prevent race conditions.
   *
   * [v3.1] REWRITTEN: The executor is now stored inside QueuedOperation so
   * the drain loop can call it. Previously, the executor was only passed to
   * processQueue() on the first call, which meant only ONE operation ever ran.
   */
  private enqueueOperation<T>(
    type: OperationType,
    executor: () => Promise<T>,
    payload?: unknown
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const operation: QueuedOperation = {
        type,
        payload,
        // Store the executor so the drain loop can call it later
        executor: executor as () => Promise<unknown>,
        resolve: () => resolve(undefined as T),
        reject,
      };

      this.operationQueue.push(operation);

      // Only the FIRST caller kicks off the drain loop.
      // While draining, subsequent callers just push and return — the loop
      // will reach their operation in FIFO order.
      if (!this.isProcessingQueue) {
        this.drainQueue();
      }
    });
  }

  /**
   * [v3.1] Drain loop: processes ALL queued operations in FIFO order.
   *
   * WHY a while-loop instead of recursion?
   * The old processQueue() ran a single operation and stopped, trusting that
   * the next enqueueOperation() call would start processing again. But if
   * multiple operations were enqueued while one was running, nobody would
   * call processQueue() for them — isProcessingQueue was already true.
   * A while-loop guarantees every enqueued operation runs exactly once.
   */
  private async drainQueue(): Promise<void> {
    this.isProcessingQueue = true;

    while (this.operationQueue.length > 0) {
      // Peek at the first operation (don't remove yet — removal after execution
      // ensures the queue length stays accurate during async execution)
      const op = this.operationQueue[0];

      try {
        await op.executor();
        op.resolve();
      } catch (error) {
        op.reject(error instanceof Error ? error : new Error(String(error)));
      }

      // Remove the completed operation from the front of the queue
      this.operationQueue.shift();
    }

    this.isProcessingQueue = false;
  }

  // ===========================================================================
  // PROMISE-BASED FADE UTILITIES
  // ===========================================================================

  /**
   * Fade a GainNode to a target value and return a Promise.
   * Uses exponentialRampToValueAtTime for smooth perceptual fades.
   */
  private fadeGain(
    gainNode: GainNode,
    targetValue: number,
    duration: number
  ): Promise<void> {
    return new Promise((resolve) => {
      if (!this.audioContext) {
        resolve();
        return;
      }

      const currentTime = this.audioContext.currentTime;
      const currentValue = gainNode.gain.value;

      // Cancel any scheduled changes
      gainNode.gain.cancelScheduledValues(currentTime);

      // Set current value explicitly
      gainNode.gain.setValueAtTime(Math.max(currentValue, 0.0001), currentTime);

      // Use exponential ramp for perceptually smooth fade
      // Note: exponentialRamp can't reach 0, so use a very small value
      const safeTarget = Math.max(targetValue, 0.0001);
      gainNode.gain.exponentialRampToValueAtTime(safeTarget, currentTime + duration);

      // If fading to "zero", set it to actual 0 after the ramp
      if (targetValue === 0) {
        gainNode.gain.setValueAtTime(0, currentTime + duration);
      }

      // Resolve after fade duration + small buffer
      setTimeout(resolve, duration * 1000 + FADE_BUFFER_MS);
    });
  }

  /**
   * [v2.1] Immediate gain set - no fade, instant value change.
   * Used when context is suspended and fades won't work.
   */
  private setGainImmediate(gainNode: GainNode, value: number): void {
    if (!this.audioContext) return;
    gainNode.gain.cancelScheduledValues(this.audioContext.currentTime);
    gainNode.gain.setValueAtTime(value, this.audioContext.currentTime);
  }

  /**
   * Fade out all active audio sources
   */
  private async fadeOutAll(duration: number = FADE.OUT): Promise<void> {
    const fadePromises: Promise<void>[] = [];

    // Fade ambience
    if (this.tracks.ambience.gainNode && this.tracks.ambience.sourceNode) {
      fadePromises.push(this.fadeGain(this.tracks.ambience.gainNode, 0, duration));
    }

    // Fade voice
    if (this.tracks.voice.gainNode && this.tracks.voice.sourceNode) {
      fadePromises.push(this.fadeGain(this.tracks.voice.gainNode, 0, duration));
    }

    // [v2.8] Fade entrainment track
    if (this.tracks.entrainment.gainNode && this.tracks.entrainment.sourceNode) {
      fadePromises.push(this.fadeGain(this.tracks.entrainment.gainNode, 0, duration));
    }

    await Promise.all(fadePromises);
  }

  /**
   * [v2.1] Set all gains to zero immediately (no fade).
   * Used when AudioContext is suspended.
   */
  private muteAllImmediate(): void {
    if (this.tracks.ambience.gainNode) {
      this.setGainImmediate(this.tracks.ambience.gainNode, 0);
    }
    if (this.tracks.voice.gainNode) {
      this.setGainImmediate(this.tracks.voice.gainNode, 0);
    }
    // [v2.8] Mute entrainment track
    if (this.tracks.entrainment.gainNode) {
      this.setGainImmediate(this.tracks.entrainment.gainNode, 0);
    }
  }

  /**
   * [v3.1] Fade in tracks that don't self-manage their own ramp.
   * Ambience, entrainment, and voice all self-manage their 3s linearRamp
   * inside their respective startXxxSource() methods.
   * This method is now a no-op but kept for structural consistency and
   * future non-self-managing tracks (e.g., a second voice layer).
   */
  private async fadeInAll(_duration: number = FADE.IN): Promise<void> {
    // All tracks now self-manage their fade-in:
    // - Ambience: 3s linearRamp in startAmbienceSource()
    // - Entrainment: 3s linearRamp in startEntrainmentSource()
    // - Voice: 3s linearRamp in startVoiceSource()
    // - Trigger: no fade (one-shot with natural envelope)
  }

  // ===========================================================================
  // INITIALIZATION
  // ===========================================================================

  public async initialize(options: EngineOptions = {}): Promise<void> {
    if (this.isInitialized) {
      console.warn('[AudioEngine] Already initialized');
      return;
    }

    try {
      // [v3.6] Reuse AudioContext if ensureContext() already created one
      // during the synchronous phase of the user gesture handler.
      // Only create a new one if nothing exists yet (defensive fallback).
      if (!this.audioContext) {
        const contextOptions: AudioContextOptions = {};
        if (options.sampleRate) {
          contextOptions.sampleRate = options.sampleRate;
        }
        const AudioCtx = window.AudioContext
          || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        this.audioContext = new AudioCtx(contextOptions);
      }

      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      // Create master gain
      this.masterGainNode = this.audioContext.createGain();
      this.masterGainNode.gain.value = options.masterVolume ?? this.volumes.master;
      this.masterGainNode.connect(this.audioContext.destination);

      // Create analyser
      this.analyserNode = this.audioContext.createAnalyser();
      this.analyserNode.fftSize = 256;
      this.masterGainNode.connect(this.analyserNode);

      // Initialize track nodes (ambience, voice, entrainment)
      this.initializeTrackNodes();

      this.isInitialized = true;
      this.engineState = 'idle';

      this.notifyStateChange();
    } catch (error) {
      console.error('[AudioEngine] Init failed:', error);
      throw error;
    }
  }

  private initializeTrackNodes(): void {
    if (!this.audioContext || !this.masterGainNode) return;

    this.tracks.ambience.gainNode = this.audioContext.createGain();
    this.tracks.ambience.gainNode.gain.value = 0; // Start at 0
    this.tracks.ambience.gainNode.connect(this.masterGainNode);

    this.tracks.voice.gainNode = this.audioContext.createGain();
    this.tracks.voice.gainNode.gain.value = 0;
    this.tracks.voice.gainNode.connect(this.masterGainNode);

    // [v2.8] Entrainment track replaces binaural oscillators
    this.tracks.entrainment.gainNode = this.audioContext.createGain();
    this.tracks.entrainment.gainNode.gain.value = 0;
    this.tracks.entrainment.gainNode.connect(this.masterGainNode);
  }

  // ===========================================================================
  // TRACK LOADING
  // ===========================================================================

  /**
   * Load a track from URL into buffer.
   * [v2.5] Added optional playId for stale request detection.
   * @param trackId - Which track to load
   * @param config - Load configuration (url, volume, etc.)
   * @param playId - Optional generation ID to validate against currentPlayId
   * @returns Promise that resolves when load completes (or is discarded if stale)
   */
  public async loadTrack(
    trackId: 'ambience' | 'voice',
    config: AudioLoadConfig,
    playId?: number
  ): Promise<void> {
    if (!this.audioContext) {
      throw new Error('AudioEngine not initialized');
    }

    const track = this.tracks[trackId];

    try {

      const response = await fetch(config.url);

      // [v2.5] CHECKPOINT 1: After fetch, check if request is stale
      if (playId !== undefined && playId !== this.currentPlayId) {
        return; // Silently abort - user changed mode
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const arrayBuffer = await response.arrayBuffer();

      // [v2.5] CHECKPOINT 2: After arrayBuffer, check again
      if (playId !== undefined && playId !== this.currentPlayId) {
        return;
      }

      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

      // [v2.5] CHECKPOINT 3: After decode, final check before applying
      if (playId !== undefined && playId !== this.currentPlayId) {
        return;
      }

      // Safe to apply - this load is still current
      track.buffer = audioBuffer;
      track.isLoaded = true;
      // [v3.1] Tag this buffer with the generation it was loaded for.
      // startAmbienceSource() will check this tag before playing.
      track.loadedForPlayId = this.currentPlayId;

      if (config.volume !== undefined) {
        this.volumes[trackId] = config.volume;
      }

      // [v3.1] AUTO-START: If the engine is already playing (mode switch while
      // playing), the buffer arrived after executePlay() ran. Start the source
      // now so audio appears seamlessly without the user pressing play again.
      if (this.engineState === 'playing' && !track.sourceNode) {
        if (trackId === 'ambience') {
          this.startAmbienceSource();
        } else if (trackId === 'voice') {
          this.startVoiceSource();
        }
      }

      this.notifyStateChange();
    } catch (error) {
      // [v2.5] Don't log error if request was intentionally aborted
      if (playId !== undefined && playId !== this.currentPlayId) {
        return;
      }
      console.error(`[AudioEngine] Load ${trackId} failed:`, error);
      throw error;
    }
  }

  /**
   * [v2.8] Load entrainment track from URL.
   * Identical pattern to loadTrack() but for the entrainment layer.
   * Uses Generation ID for stale request detection.
   *
   * @param url - URL to the .ogg (or .mp3) entrainment file
   * @param playId - Optional generation ID for stale detection
   */
  public async loadEntrainment(url: string, playId?: number): Promise<void> {
    if (!this.audioContext) {
      throw new Error('AudioEngine not initialized');
    }

    const track = this.tracks.entrainment;

    try {

      const response = await fetch(url);

      // CHECKPOINT 1: After fetch
      if (playId !== undefined && playId !== this.currentPlayId) {
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const arrayBuffer = await response.arrayBuffer();

      // CHECKPOINT 2: After arrayBuffer
      if (playId !== undefined && playId !== this.currentPlayId) {
        return;
      }

      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

      // CHECKPOINT 3: After decode
      if (playId !== undefined && playId !== this.currentPlayId) {
        return;
      }

      track.buffer = audioBuffer;
      track.isLoaded = true;
      track.loadedForPlayId = this.currentPlayId; // [v3.1] Tag with generation

      this.notifyStateChange();

      // [v3.1] AUTO-START: If the engine is already playing and no source is
      // running, start this entrainment source now. This handles the case where
      // executePlay() ran before the load completed (it skipped the stale buffer).
      if (this.engineState === 'playing' && !track.sourceNode) {
        this.startEntrainmentSource();
      }
    } catch (error) {
      if (playId !== undefined && playId !== this.currentPlayId) {
        return;
      }
      console.error('[AudioEngine] Entrainment load failed:', error);
      throw error;
    }
  }

  /**
   * [v2.9] Load a one-shot trigger sound (e.g. Tibetan bowl) from URL.
   * The buffer is stored in triggerBuffer and played by startTriggerSource().
   * Uses Generation ID stale detection - identical pattern to loadEntrainment().
   *
   * @param url - URL to the trigger audio file (.ogg / .mp3)
   * @param playId - Optional generation ID for stale detection
   */
  public async loadTrigger(url: string, playId?: number): Promise<void> {
    if (!this.audioContext) {
      throw new Error('AudioEngine not initialized');
    }

    try {

      const response = await fetch(url);

      // CHECKPOINT 1: After fetch
      if (playId !== undefined && playId !== this.currentPlayId) {
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const arrayBuffer = await response.arrayBuffer();

      // CHECKPOINT 2: After arrayBuffer
      if (playId !== undefined && playId !== this.currentPlayId) {
        return;
      }

      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

      // CHECKPOINT 3: After decode
      if (playId !== undefined && playId !== this.currentPlayId) {
        return;
      }

      this.triggerBuffer = audioBuffer;
      this.triggerLoadedForPlayId = this.currentPlayId; // [v3.1] Tag with generation

      // [v3.1] AUTO-START: If engine is playing and trigger hasn't played yet
      if (this.engineState === 'playing' && !this.triggerNode) {
        this.startTriggerSource();
      }
    } catch (error) {
      if (playId !== undefined && playId !== this.currentPlayId) {
        return;
      }
      console.error('[AudioEngine] Trigger load failed:', error);
      throw error;
    }
  }

  // ===========================================================================
  // [v2.1] TRACK CLEARING - Fix for "sticky ambience" bug
  // ===========================================================================

  /**
   * Clear a track's buffer and stop its source node.
   * Use this when changing to a mode that doesn't have a track loaded.
   */
  public clearTrack(trackId: 'ambience' | 'voice' | 'entrainment'): void {
    const track = this.tracks[trackId];

    // Stop and disconnect source if playing
    if (track.sourceNode) {
      try {
        track.sourceNode.stop();
        track.sourceNode.disconnect();
      } catch {
        // Ignore - source may already be stopped
      }
      track.sourceNode = null;
    }

    // Clear buffer and reset generation tag
    track.buffer = null;
    track.isLoaded = false;
    track.loadedForPlayId = 0; // [v3.1] Reset tag — no valid generation

    this.notifyStateChange();
  }

  /**
   * Check if a track has a loaded buffer
   */
  public hasTrackLoaded(trackId: 'ambience' | 'voice' | 'entrainment'): boolean {
    return this.tracks[trackId].isLoaded && this.tracks[trackId].buffer !== null;
  }

  /**
   * [v2.9] Stop the active trigger node and clear the buffer.
   * Use when switching to a mode that has no trigger, or on stop.
   */
  public clearTrigger(): void {
    if (this.triggerNode) {
      try {
        this.triggerNode.stop();
        this.triggerNode.disconnect();
      } catch {
        // Ignore - may already have finished playing naturally
      }
      this.triggerNode = null;
    }
    this.triggerBuffer = null;
    this.triggerLoadedForPlayId = 0; // [v3.1] Reset generation tag
  }

  /**
   * [v2.3] Hard clear ALL buffers - used in "hard stop" before mode change.
   * This ensures no residual audio signal remains in the buffer.
   * [v3.1] Also resets loadedForPlayId so stale buffers can't be replayed
   * by auto-start or startSource methods after a mode change.
   */
  private hardClearAllBuffers(): void {
    if (this.tracks.ambience.buffer) {
      this.tracks.ambience.buffer = null;
      this.tracks.ambience.isLoaded = false;
      this.tracks.ambience.loadedForPlayId = 0; // [v3.1] Invalidate generation
    }
    if (this.tracks.voice.buffer) {
      this.tracks.voice.buffer = null;
      this.tracks.voice.isLoaded = false;
      this.tracks.voice.loadedForPlayId = 0; // [v3.1] Invalidate generation
    }
    if (this.tracks.entrainment.buffer) {
      this.tracks.entrainment.buffer = null;
      this.tracks.entrainment.isLoaded = false;
      this.tracks.entrainment.loadedForPlayId = 0; // [v3.1] Invalidate generation
    }
    // [v2.9] Clear trigger buffer (next mode may have a different trigger)
    if (this.triggerBuffer) {
      this.triggerBuffer = null;
      this.triggerLoadedForPlayId = 0; // [v3.1] Invalidate generation
    }
  }

  // ===========================================================================
  // PLAYBACK CONTROL - All operations go through the queue
  // ===========================================================================

  /**
   * Start playback with specified mode.
   * If already playing, this will do a clean crossfade to the new mode.
   */
  public play(mode?: AudioMode): Promise<void> {
    const targetMode = mode ?? this.currentMode ?? 'focus';

    return this.enqueueOperation('play', async () => {
      await this.executePlay(targetMode);
    });
  }

  /**
   * Internal play execution - called by queue processor
   * [v3.1] REWRITTEN: No longer increments currentPlayId (that's setMode's job).
   * Incrementing here was DESTROYING in-flight loads that Player.tsx just started.
   * Now we just READ currentPlayId as a snapshot to detect if setMode() runs
   * during our async operations (which means user switched mode — abort).
   */
  private async executePlay(targetMode: AudioMode): Promise<void> {
    if (!this.isInitialized || !this.audioContext) {
      throw new Error('Engine not initialized');
    }

    // [v3.1] READ-ONLY snapshot of the current generation.
    // DO NOT increment here — setMode() is the only place that increments.
    // Incrementing here was Bug #2: it invalidated the loads that Player.tsx
    // started for this exact mode via the auto-load effect.
    const playId = this.currentPlayId;

    // [v2.4] MOBILE FIX: Resume AudioContext IMMEDIATELY (iOS/Android requirement)
    // This must happen before any audio operations
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    // [v2.5] CHECK: If another play started while we were resuming context, abort
    if (playId !== this.currentPlayId) {
      return;
    }

    // [v2.3] TRANSITION LOCK: Prevent concurrent mode changes
    if (this._isTransitioning) {
      console.warn('[AudioEngine] Play request ignored - Transition already in progress');
      return;
    }

    this._isTransitioning = true;

    try {

      const wasPlaying = this.engineState === 'playing';
      const wasPaused = this.engineState === 'paused';
      const hasActiveAudio = wasPlaying || wasPaused;

      this.engineState = 'transitioning';
      this.notifyStateChange();

      // =========================================================================
      // [v2.4] CRITICAL: SCORCHED EARTH POLICY - Kill ALL audio BEFORE new mode
      // This prevents "audio bleeding" where old ambience sounds during loading
      // =========================================================================
      if (hasActiveAudio) {

        // STEP 1: INSTANT FADE OUT on ambience (0.1s to prevent click)
        if (this.tracks.ambience.gainNode && this.tracks.ambience.sourceNode) {
          const ctx = this.audioContext;
          const gain = this.tracks.ambience.gainNode.gain;
          gain.cancelScheduledValues(ctx.currentTime);
          gain.setValueAtTime(gain.value, ctx.currentTime);
          gain.linearRampToValueAtTime(0, ctx.currentTime + 0.1);
          await this.sleep(100); // Wait for fade
        }

        // STEP 2: KILL ambience source IMMEDIATELY
        if (this.tracks.ambience.sourceNode) {
          try {
            this.tracks.ambience.sourceNode.stop();
            this.tracks.ambience.sourceNode.disconnect();
          } catch {
            // Ignore - may already be stopped
          }
          this.tracks.ambience.sourceNode = null;
        }

        // STEP 3: NULLIFY ambience buffer (prevent reuse)
        this.tracks.ambience.buffer = null;
        this.tracks.ambience.isLoaded = false;

        // STEP 4: Mute ALL other sources IMMEDIATELY
        this.muteAllImmediate();
        if (this.masterGainNode) {
          this.setGainImmediate(this.masterGainNode, 0);
        }

        // STEP 5: Stop and disconnect ALL remaining sources
        this.stopSourcesImmediate();

        // STEP 6: Clear all remaining buffers
        this.hardClearAllBuffers();

        // STEP 7: Clean separation delay
        await this.sleep(50);

        // STEP 8: Restore master gain for new audio
        if (this.masterGainNode) {
          this.masterGainNode.gain.setValueAtTime(
            this.volumes.master,
            this.audioContext.currentTime
          );
        }

      }

      // Update mode
      this.currentMode = targetMode;

      // DEFENSIVE: Stop any residual ambience source before starting new mode.
      // When coming from idle (stop → play), stopSourcesImmediate() already null'd
      // the sourceNode, so this is a no-op. When some edge-case left a sourceNode
      // alive (e.g., rapid mode switches), this kills it cleanly.
      // NOTE: We do NOT clear the buffer here - Player.tsx is responsible for
      // setting/clearing the ambience buffer via loadAmbience() / clearAmbience().
      if (this.tracks.ambience.sourceNode) {
        try {
          this.tracks.ambience.sourceNode.stop();
          this.tracks.ambience.sourceNode.disconnect();
        } catch {
          // Ignore - may already be stopped
        }
        this.tracks.ambience.sourceNode = null;
      }

      // Start all audio layers for the new mode
      this.startAmbienceSource();
      this.startEntrainmentSource(); // [v2.8] handles its own 3s fade-in
      this.startTriggerSource();     // [v2.9] one-shot anchor, plays immediately
      this.startVoiceSource();       // Voice guide (one-shot, self-manages fade-in)

      // Fade in (only affects tracks that don't self-manage their fade)
      await this.fadeInAll(FADE.IN);

      this.engineState = 'playing';
    } catch (error) {
      this.engineState = 'idle';
      throw error;
    } finally {
      // [v2.3] ALWAYS release transition lock
      this._isTransitioning = false;
    }

    this.notifyStateChange();
  }

  /**
   * [v2.1] Play from paused state - handles the "smart resume" scenario.
   * This method properly handles the case where the mode changed while paused.
   */
  public playFromPaused(mode: AudioMode): Promise<void> {
    return this.enqueueOperation('playFromPaused', async () => {
      await this.executePlayFromPaused(mode);
    });
  }

  /**
   * [v2.1] Internal execution for play-from-paused.
   * [v2.3] REFACTORED: Added transition lock for consistency
   * [v2.4] MOBILE FIX: Explicit context resume
   * [v2.7] FIX: Check if ambience buffer is loaded, fall back to full play if not
   * [v2.7.1] ZOMBIE PAUSE FIX: Do NOT clear buffers - new mode already loaded
   */
  private async executePlayFromPaused(targetMode: AudioMode): Promise<void> {
    if (!this.isInitialized || !this.audioContext) {
      throw new Error('Engine not initialized');
    }

    // [v2.4] MOBILE FIX: Resume AudioContext FIRST (iOS/Android requirement)
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    // Only proceed if we're actually paused
    if (this.engineState !== 'paused') {
      console.warn('[AudioEngine] playFromPaused called but not in paused state');
      // Fall back to regular play
      return this.executePlay(targetMode);
    }

    // [v2.7] FIX: If ambience buffer isn't loaded for target mode, use full play instead
    // This handles the case where user changes mode while paused
    if (!this.tracks.ambience.buffer || !this.tracks.ambience.isLoaded) {
      // Release any transition lock before calling executePlay
      return this.executePlay(targetMode);
    }

    // [v2.3] TRANSITION LOCK
    if (this._isTransitioning) {
      console.warn('[AudioEngine] PlayFromPaused ignored - Transition in progress');
      return;
    }

    this._isTransitioning = true;

    try {
      this.engineState = 'transitioning';
      this.notifyStateChange();

      // STEP 1: Mute all gains IMMEDIATELY before resuming context
      // This prevents any sound from the old sources when context resumes
      this.muteAllImmediate();
      if (this.masterGainNode) {
        this.setGainImmediate(this.masterGainNode, 0);
      }

      // STEP 2: Ensure AudioContext is resumed (double-check)
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      // STEP 3: Stop all old sources (they're muted, so no click)
      this.stopSourcesImmediate();

      // [v2.7] FIX: DO NOT clear buffers here!
      // When changing modes from pause, we've already loaded the NEW mode's buffers.
      // Clearing them here would cause the "zombie pause state" bug where ambience doesn't play.
      // The old sources are already stopped and disconnected, so there's no residual audio.

      // Small gap for clean state
      await this.sleep(10);

      // STEP 4: Update mode and start new sources
      this.currentMode = targetMode;

      // DEFENSIVE: Kill any residual ambience sourceNode before starting
      if (this.tracks.ambience.sourceNode) {
        try {
          this.tracks.ambience.sourceNode.stop();
          this.tracks.ambience.sourceNode.disconnect();
        } catch {
          // Ignore
        }
        this.tracks.ambience.sourceNode = null;
      }

      this.startAmbienceSource();
      this.startEntrainmentSource(); // [v2.8] handles its own 3s fade-in
      this.startTriggerSource();     // [v2.9] one-shot anchor
      this.startVoiceSource();       // Voice guide (one-shot, self-manages fade-in)

      // STEP 5: [v2.7] CLICK FIX: Smooth restore of master gain, then fade in
      if (this.masterGainNode) {
        const currentTime = this.audioContext.currentTime;
        this.masterGainNode.gain.cancelScheduledValues(currentTime);
        this.masterGainNode.gain.setValueAtTime(0, currentTime);
        this.masterGainNode.gain.linearRampToValueAtTime(this.volumes.master, currentTime + FADE.INSTANT);
        await this.sleep(FADE.INSTANT * 1000);
      }

      await this.fadeInAll(FADE.IN);

      this.engineState = 'playing';
    } catch (error) {
      this.engineState = 'idle';
      throw error;
    } finally {
      // [v2.3] ALWAYS release transition lock
      this._isTransitioning = false;
    }

    this.notifyStateChange();
  }

  /**
   * Stop all playback with fade out.
   */
  public stop(): Promise<void> {
    return this.enqueueOperation('stop', async () => {
      await this.executeStop();
    });
  }

  private async executeStop(): Promise<void> {
    if (this.engineState === 'idle') return;

    const wasPaused = this.engineState === 'paused';

    this.engineState = 'transitioning';
    this.notifyStateChange();

    // [v2.1] If we were paused, resume context first so fades work
    if (wasPaused && this.audioContext?.state === 'suspended') {
      // Mute immediately before resuming to prevent any sound
      this.muteAllImmediate();
      await this.audioContext.resume();
    }

    // Fade out all audio
    await this.fadeOutAll(FADE.OUT);

    // Stop sources (they're at 0 volume now)
    this.stopSourcesImmediate();

    // [v3.3] Session ended — allow trigger to play on next session start
    this.triggerHasPlayedForSession = false;

    this.engineState = 'idle';
    this.notifyStateChange();
  }

  /**
   * Pause playback - suspends AudioContext
   */
  public pause(): Promise<void> {
    return this.enqueueOperation('pause', async () => {
      await this.executePause();
    });
  }

  private async executePause(): Promise<void> {
    if (!this.audioContext || this.engineState !== 'playing') return;

    this.engineState = 'transitioning';
    this.notifyStateChange();

    // [v2.7] CLICK FIX: Smooth fade out on master before suspend
    if (this.masterGainNode) {
      await this.fadeGain(this.masterGainNode, 0, FADE.OUT);
    }

    // Suspend context (audio is already at 0 volume from fade)
    await this.audioContext.suspend();

    this.engineState = 'paused';
    this.notifyStateChange();
  }

  /**
   * Resume playback from pause
   */
  public resume(): Promise<void> {
    return this.enqueueOperation('resume', async () => {
      await this.executeResume();
    });
  }

  private async executeResume(): Promise<void> {
    if (!this.audioContext || this.engineState !== 'paused') return;

    this.engineState = 'transitioning';
    this.notifyStateChange();

    // [v2.7] CLICK FIX: Ensure master is at 0 before resuming context
    if (this.masterGainNode) {
      const currentTime = this.audioContext.currentTime;
      this.masterGainNode.gain.cancelScheduledValues(currentTime);
      this.masterGainNode.gain.setValueAtTime(0, currentTime);
    }

    // [v2.4] MOBILE FIX: Resume context after ensuring 0 volume
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    // [v3.0] Smooth 3-second fade-in from 0 to target volume (linearRamp).
    // Matches the initial play fade-in. Prevents sudden volume jump on unpause.
    // We schedule the ramp and set state to 'playing' immediately for responsive UI.
    if (this.masterGainNode) {
      const ct = this.audioContext.currentTime;
      this.masterGainNode.gain.cancelScheduledValues(ct);
      this.masterGainNode.gain.setValueAtTime(0, ct);
      this.masterGainNode.gain.linearRampToValueAtTime(this.volumes.master, ct + LOOP_FADE_IN);
    }

    this.engineState = 'playing';
    this.notifyStateChange();
  }

  // ===========================================================================
  // SOURCE NODE MANAGEMENT
  // ===========================================================================

  /**
   * Start ambience source node with native looping and 3s fade-in.
   * [v3.0] REWRITTEN: Matches entrainment pattern - self-manages fade-in via
   * linearRampToValueAtTime. The gain stays at targetVolume indefinitely while
   * the native loop=true handles sample-accurate looping.
   * NO onended, NO setTimeout, NO manual restart. Trust the browser's native loop.
   *
   * [v3.1] GUARD: Checks loadedForPlayId against currentPlayId before starting.
   * If the buffer was loaded for a PREVIOUS mode (stale generation), it is NOT
   * played. This prevents "zombie ambience" where Focus's brown noise plays
   * during Sleep mode because the old buffer was still sitting in memory.
   */
  private startAmbienceSource(): void {
    if (!this.audioContext) return;

    const track = this.tracks.ambience;

    if (!track.buffer || !track.gainNode) {
      return;
    }

    // [v3.1] STALE BUFFER GUARD: If this buffer was loaded for a different
    // generation (mode), do NOT play it. The auto-load effect in Player.tsx
    // will eventually load the correct buffer for the current mode.
    if (track.loadedForPlayId !== this.currentPlayId) {
      return;
    }

    // Disconnect old source if exists
    if (track.sourceNode) {
      try {
        track.sourceNode.stop();
        track.sourceNode.disconnect();
      } catch {
        // Ignore
      }
    }

    const ctx = this.audioContext;

    // CRITICAL: Start at gain 0 to prevent click
    track.gainNode.gain.cancelScheduledValues(ctx.currentTime);
    track.gainNode.gain.setValueAtTime(0, ctx.currentTime);

    // Create new source - NATIVE loop only, no manual restart
    track.sourceNode = ctx.createBufferSource();
    track.sourceNode.buffer = track.buffer;
    track.sourceNode.loop = true;
    // [v3.2] Explicit loop points to guard against codec padding artifacts.
    // Some Opus decoders add silent samples at the buffer tail; setting loopEnd
    // to the exact decoded duration ensures the browser doesn't overshoot.
    track.sourceNode.loopStart = 0;
    track.sourceNode.loopEnd = track.buffer.duration;
    track.sourceNode.connect(track.gainNode);
    track.sourceNode.start();

    // [v3.0] 3-second fade-in (linearRamp) - gain stays at target indefinitely after
    track.gainNode.gain.linearRampToValueAtTime(
      this.volumes.ambience,
      ctx.currentTime + LOOP_FADE_IN
    );

    // [v3.2] Diagnostic: log decoded buffer details to detect codec padding
  }

  /**
   * [v2.8] Start entrainment source node from loaded buffer.
   * - Sets gain to 0 FIRST (anti-click)
   * - Loops indefinitely (loop = true)
   * - Applies 3-second fade-in to avoid harsh loop start artifact
   * - If no buffer is loaded (mode has no entrainment file), silently skips
   *
   * [v3.1] GUARD: Validates loadedForPlayId before starting. A stale buffer
   * (loaded for a previous mode's generation) is silently skipped.
   */
  private startEntrainmentSource(): void {
    if (!this.audioContext) return;

    const track = this.tracks.entrainment;

    if (!track.buffer || !track.gainNode) {
      return;
    }

    // [v3.1] STALE BUFFER GUARD: Don't play a buffer from a previous mode
    if (track.loadedForPlayId !== this.currentPlayId) {
      return;
    }

    // Disconnect old source if exists
    if (track.sourceNode) {
      try {
        track.sourceNode.stop();
        track.sourceNode.disconnect();
      } catch {
        // Ignore
      }
    }

    const ctx = this.audioContext;

    // CRITICAL: Start at gain 0 to prevent click
    track.gainNode.gain.cancelScheduledValues(ctx.currentTime);
    track.gainNode.gain.setValueAtTime(0, ctx.currentTime);

    // Create new source node
    track.sourceNode = ctx.createBufferSource();
    track.sourceNode.buffer = track.buffer;
    track.sourceNode.loop = true;
    // [v3.2] Explicit loop points to guard against codec padding artifacts
    track.sourceNode.loopStart = 0;
    track.sourceNode.loopEnd = track.buffer.duration;
    track.sourceNode.connect(track.gainNode);
    track.sourceNode.start();

    // [v2.8] Anti-click: 3-second fade-in for loop start
    track.gainNode.gain.linearRampToValueAtTime(
      this.volumes.tone,
      ctx.currentTime + LOOP_FADE_IN
    );

    // [v3.2] Diagnostic: log decoded buffer details to detect codec padding
  }

  /**
   * [v2.9] Start the one-shot trigger source node.
   * - Plays exactly once (loop = false)
   * - Connects directly to masterGainNode (bypasses track gain routing)
   * - No fade applied - the audio file carries its own natural envelope
   * - Silently skips if no trigger buffer has been loaded
   *
   * [v3.1] GUARD: Validates triggerLoadedForPlayId before starting.
   * A stale trigger buffer (loaded for a previous mode) is skipped.
   */
  private startTriggerSource(): void {
    if (!this.audioContext || !this.masterGainNode) return;

    if (!this.triggerBuffer) {
      return;
    }

    // [v3.1] STALE BUFFER GUARD: Don't play a trigger from a previous mode
    if (this.triggerLoadedForPlayId !== this.currentPlayId) {
      return;
    }

    // [v3.3] SESSION GUARD: Only play the trigger once per session.
    // The trigger is a Pavlovian anchor (e.g., Tibetan bowl) that sounds
    // exactly once when a new session starts. After the one-shot finishes
    // naturally (onended nulls triggerNode), nothing should replay it —
    // not component remounts, not auto-load effects, not re-renders.
    if (this.triggerHasPlayedForSession) {
      return;
    }

    // Stop any previous trigger that may still be decaying
    if (this.triggerNode) {
      try {
        this.triggerNode.stop();
        this.triggerNode.disconnect();
      } catch {
        // Ignore - may already have finished
      }
    }

    this.triggerNode = this.audioContext.createBufferSource();
    this.triggerNode.buffer = this.triggerBuffer;
    this.triggerNode.loop = false; // One-shot - plays exactly once
    this.triggerNode.connect(this.masterGainNode);
    this.triggerNode.start(0);
    this.triggerHasPlayedForSession = true; // [v3.3] Mark as played for this session

    // Self-cleanup: null out the reference once it finishes naturally
    this.triggerNode.onended = () => {
      this.triggerNode = null;
    };

  }

  /**
   * Start voice source node from loaded buffer.
   * Unlike ambience/entrainment which loop indefinitely, the voice track
   * plays once (loop = false) — it's a grounding guide, not a drone.
   * Uses the same playId guard pattern to prevent stale buffers from playing.
   *
   * Fade-in is handled externally by fadeInAll() (which ramps voice gain).
   * The voice gain starts at 0 (set during initializeTrackNodes) and
   * fadeInAll() ramps it to this.volumes.voice over FADE.IN duration.
   */
  private startVoiceSource(): void {
    if (!this.audioContext) return;

    const track = this.tracks.voice;

    if (!track.buffer || !track.gainNode) {
      return;
    }

    // [v3.1] STALE BUFFER GUARD: Don't play a voice from a previous mode
    if (track.loadedForPlayId !== this.currentPlayId) {
      return;
    }

    // Disconnect old source if exists
    if (track.sourceNode) {
      try {
        track.sourceNode.stop();
        track.sourceNode.disconnect();
      } catch {
        // Ignore
      }
    }

    const ctx = this.audioContext;

    // Start at gain 0 — fadeInAll() will ramp to target volume
    track.gainNode.gain.cancelScheduledValues(ctx.currentTime);
    track.gainNode.gain.setValueAtTime(0, ctx.currentTime);

    // Create new source — one-shot (loop = false), plays the guide once
    track.sourceNode = ctx.createBufferSource();
    track.sourceNode.buffer = track.buffer;
    track.sourceNode.loop = false; // Voice guide plays once, not indefinitely
    track.sourceNode.connect(track.gainNode);
    track.sourceNode.start();

    // Self-cleanup: null out the sourceNode when the guide finishes
    track.sourceNode.onended = () => {
      track.sourceNode = null;
    };

    // [v3.4] Respect voice mute state: if muted, keep gain at 0
    const targetVolume = this.voiceMuted ? 0 : this.volumes.voice;

    // Fade in the voice track over LOOP_FADE_IN (3s) for a smooth entrance
    track.gainNode.gain.linearRampToValueAtTime(
      targetVolume,
      ctx.currentTime + LOOP_FADE_IN
    );

  }

  /**
   * Stop all sources immediately (call only when volume is 0)
   */
  private stopSourcesImmediate(): void {
    // Stop ambience
    if (this.tracks.ambience.sourceNode) {
      try {
        this.tracks.ambience.sourceNode.stop();
        this.tracks.ambience.sourceNode.disconnect();
      } catch {
        // Ignore
      }
      this.tracks.ambience.sourceNode = null;
    }

    // Stop voice
    if (this.tracks.voice.sourceNode) {
      try {
        this.tracks.voice.sourceNode.stop();
        this.tracks.voice.sourceNode.disconnect();
      } catch {
        // Ignore
      }
      this.tracks.voice.sourceNode = null;
    }

    // [v2.8] Stop entrainment
    if (this.tracks.entrainment.sourceNode) {
      try {
        this.tracks.entrainment.sourceNode.stop();
        this.tracks.entrainment.sourceNode.disconnect();
      } catch {
        // Ignore
      }
      this.tracks.entrainment.sourceNode = null;
    }

    // [v2.9] Stop trigger if still decaying
    if (this.triggerNode) {
      try {
        this.triggerNode.stop();
        this.triggerNode.disconnect();
      } catch {
        // Ignore - may have already finished naturally
      }
      this.triggerNode = null;
    }
  }

  // ===========================================================================
  // VOLUME CONTROL
  // ===========================================================================

  public setTrackVolume(trackId: TrackId, volume: number, rampTime: number = 0.1): void {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    this.volumes[trackId] = clampedVolume;

    if (!this.audioContext) return;

    let gainNode: GainNode | null = null;

    // [v2.8] 'tone' TrackId now maps to entrainment gainNode
    if (trackId === 'tone') {
      gainNode = this.tracks.entrainment.gainNode;
    } else {
      gainNode = this.tracks[trackId].gainNode;
    }

    if (gainNode && this.engineState === 'playing') {
      const currentTime = this.audioContext.currentTime;
      gainNode.gain.cancelScheduledValues(currentTime);
      gainNode.gain.setValueAtTime(gainNode.gain.value, currentTime);
      gainNode.gain.linearRampToValueAtTime(clampedVolume, currentTime + rampTime);
    }

    this.notifyStateChange();
  }

  public setMasterVolume(volume: number, rampTime: number = 0.1): void {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    this.volumes.master = clampedVolume;

    if (!this.audioContext || !this.masterGainNode) return;

    const currentTime = this.audioContext.currentTime;
    this.masterGainNode.gain.cancelScheduledValues(currentTime);
    this.masterGainNode.gain.setValueAtTime(this.masterGainNode.gain.value, currentTime);
    this.masterGainNode.gain.linearRampToValueAtTime(clampedVolume, currentTime + rampTime);

    this.notifyStateChange();
  }

  public getTrackVolume(trackId: TrackId): number {
    return this.volumes[trackId];
  }

  public getMasterVolume(): number {
    return this.volumes.master;
  }

  /**
   * [v3.4] Mute or unmute the voice track with a smooth fade.
   * Used by the "Voz Guía" toggle in anxiety mode.
   * When muted, voice gain fades to 0 over VOICE_MUTE_FADE (0.5s).
   * When unmuted, voice gain fades back to this.volumes.voice.
   */
  public setVoiceMuted(isMuted: boolean): void {
    this.voiceMuted = isMuted;

    if (!this.audioContext) return;

    const gainNode = this.tracks.voice.gainNode;
    if (!gainNode) return;

    // Only apply gain change if engine is currently playing
    if (this.engineState === 'playing') {
      const currentTime = this.audioContext.currentTime;
      const targetVolume = isMuted ? 0 : this.volumes.voice;
      gainNode.gain.cancelScheduledValues(currentTime);
      gainNode.gain.setValueAtTime(gainNode.gain.value, currentTime);
      gainNode.gain.linearRampToValueAtTime(targetVolume, currentTime + VOICE_MUTE_FADE);

    }
  }

  /**
   * [v3.5] Transition entrainment track for Pomodoro phase changes.
   *
   * - 'break' phase: fade entrainment gain to 0 (silence isochronic pulses,
   *   let ambient layer continue for neurological recovery).
   * - 'work' phase: ramp entrainment gain back to its designated volume
   *   (this.volumes.tone) for renewed cognitive entrainment.
   *
   * Uses linearRampToValueAtTime for click-free, perceptually smooth fades.
   * The ambience gain node is intentionally untouched.
   */
  public transitionPomodoroPhase(
    phase: 'work' | 'break',
    transitionDuration: number = POMODORO_TRANSITION_FADE
  ): void {
    if (!this.audioContext) return;

    const gainNode = this.tracks.entrainment.gainNode;
    if (!gainNode) return;

    // Only apply if engine is actively playing
    if (this.engineState !== 'playing') return;

    const currentTime = this.audioContext.currentTime;
    const targetVolume = phase === 'break' ? 0 : this.volumes.tone;

    // Cancel any in-flight ramps, anchor at current value, then ramp
    gainNode.gain.cancelScheduledValues(currentTime);
    gainNode.gain.setValueAtTime(gainNode.gain.value, currentTime);
    gainNode.gain.linearRampToValueAtTime(targetVolume, currentTime + transitionDuration);
  }

  // ===========================================================================
  // MODE MANAGEMENT - Does NOT auto-play
  // ===========================================================================

  /**
   * Set the current mode WITHOUT starting playback.
   * This only updates the internal mode reference.
   * [v2.5] Also increments playId to invalidate stale loads for previous mode.
   */
  public setMode(mode: AudioMode): void {
    // [v2.5] Increment generation ID to invalidate pending loads for old mode
    ++this.currentPlayId;
    // [v3.3] New mode = allow the trigger to play when the session starts
    this.triggerHasPlayedForSession = false;

    this.currentMode = mode;
    this.notifyStateChange();
  }

  public getCurrentMode(): AudioMode | null {
    return this.currentMode;
  }

  public getEngineState(): EngineState {
    return this.engineState;
  }

  /**
   * [v2.5] Get the current generation ID for stale request detection.
   * External code should call this before starting a load, then pass
   * the ID to loadTrack() for validation.
   */
  public getCurrentPlayId(): number {
    return this.currentPlayId;
  }

  // ===========================================================================
  // FADE EFFECTS
  // ===========================================================================

  public async fadeOut(duration: number = 5): Promise<void> {
    if (!this.audioContext || !this.masterGainNode) return;

    await this.fadeGain(this.masterGainNode, 0, duration);
    await this.stop();

    // Restore master volume
    this.masterGainNode.gain.setValueAtTime(this.volumes.master, this.audioContext.currentTime);
  }

  // ===========================================================================
  // STATE SUBSCRIPTION
  // ===========================================================================

  public subscribe(callback: EngineStateCallback): () => void {
    this.stateCallbacks.add(callback);
    callback(this.getPublicState());
    return () => {
      this.stateCallbacks.delete(callback);
    };
  }

  private notifyStateChange(): void {
    const state = this.getPublicState();
    this.stateCallbacks.forEach((cb) => cb(state));
  }

  public getPublicState(): EnginePublicState {
    return {
      isInitialized: this.isInitialized,
      engineState: this.engineState,
      isPlaying: this.engineState === 'playing',
      isPaused: this.engineState === 'paused',
      currentMode: this.currentMode,
      isTransitioning: this.engineState === 'transitioning',
      tracks: {
        ambience: {
          volume: this.volumes.ambience,
          isLoaded: this.tracks.ambience.isLoaded,
        },
        // [v2.8] 'tone' now reflects the file-based entrainment track
        tone: {
          volume: this.volumes.tone,
          isLoaded: this.tracks.entrainment.isLoaded,
        },
        voice: {
          volume: this.volumes.voice,
          isLoaded: this.tracks.voice.isLoaded,
        },
      },
      master: {
        volume: this.volumes.master,
      },
    };
  }

  // ===========================================================================
  // UTILITIES
  // ===========================================================================

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  public getAudioContext(): AudioContext | null {
    return this.audioContext;
  }

  /**
   * [v2.6] MOBILE FIX: "Silent Unlocker" pattern for iOS Safari.
   * CRITICAL: Must be called SYNCHRONOUSLY from user gesture (click/touch handler).
   *
   * This method uses the "silent buffer trick" to force the audio hardware to wake up:
   * 1. Resume the context (if suspended)
   * 2. Create a silent buffer source and play it immediately (0.001s duration)
   * 3. This tricks Safari into unlocking audio by showing "real" playback intent
   *
   * This method intentionally does NOT use async/await to maintain the browser's
   * "User Gesture" trust chain.
   *
   * Usage: Call this as the FIRST line in any click handler before any async operations.
   */
  /**
   * [v3.6] Synchronous AudioContext creation + resume for mobile unlock.
   *
   * CRITICAL: This method MUST be called as the very first line inside every
   * user-gesture handler (click, touchstart, keydown) BEFORE any async
   * operation, state read, or React setState.
   *
   * Why it works:
   *   Mobile browsers (iOS Safari, Chrome Android) require `new AudioContext()`
   *   or `audioContext.resume()` to run synchronously within the call stack of
   *   a trusted user event.  The old `unlock()` bailed out when audioContext
   *   was null (first tap), and `initialize()` created the context inside an
   *   async function — by which time the gesture token had expired.
   *
   *   `ensureContext()` is fully synchronous: it creates the AudioContext,
   *   fires `.resume()`, and plays a silent buffer — all in the same
   *   microtask, preserving the gesture trust chain.
   *
   * The later `initialize()` call reuses this already-running context.
   */
  public ensureContext(): void {
    // STEP 1: Create AudioContext synchronously if it doesn't exist yet
    if (!this.audioContext) {
      const AudioCtx = window.AudioContext
        || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.audioContext = new AudioCtx();
    }

    // STEP 2: Resume context synchronously (fire-and-forget the promise)
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }

    // STEP 3: Play a silent "ghost sound" to force hardware wake-up (iOS Safari)
    // A single-sample silent buffer tricks the OS audio session into activating
    const buffer = this.audioContext.createBuffer(1, 1, this.audioContext.sampleRate);
    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;
    source.connect(this.audioContext.destination);
    source.start(0);
    source.stop(this.audioContext.currentTime + 0.001);
  }

  /**
   * @deprecated Use ensureContext() instead — kept for backwards compatibility.
   */
  public unlock(): void {
    this.ensureContext();
  }

  /**
   * @deprecated Use ensureContext() instead — kept for backwards compatibility.
   */
  public resumeContext(): void {
    this.ensureContext();
  }

  public getAnalyser(): AnalyserNode | null {
    return this.analyserNode;
  }

  public getIsInitialized(): boolean {
    return this.isInitialized;
  }

  // ===========================================================================
  // CLEANUP
  // ===========================================================================

  public dispose(): void {

    // Clear queue
    this.operationQueue = [];
    this.isProcessingQueue = false;

    // Stop all
    this.stopSourcesImmediate();

    // Close context
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    // Clear references
    this.masterGainNode = null;
    this.analyserNode = null;
    this.tracks.ambience = { gainNode: null, sourceNode: null, buffer: null, isLoaded: false, loadedForPlayId: 0 };
    this.tracks.voice = { gainNode: null, sourceNode: null, buffer: null, isLoaded: false, loadedForPlayId: 0 };
    this.tracks.entrainment = { gainNode: null, sourceNode: null, buffer: null, isLoaded: false, loadedForPlayId: 0 };
    this.triggerNode = null;   // [v2.9]
    this.triggerBuffer = null; // [v2.9]
    this.triggerLoadedForPlayId = 0; // [v3.1] Reset generation tag
    this.triggerHasPlayedForSession = false; // [v3.3] Reset session guard
    this.voiceMuted = false; // [v3.4] Reset voice mute state
    this.stateCallbacks.clear();

    this.isInitialized = false;
    this.engineState = 'idle';

  }
}

// =============================================================================
// EXPORT
// =============================================================================

export const getAudioEngine = (): AudioEngine => AudioEngine.getInstance();

export default AudioEngine;